import { useEffect, useState } from "react";

import { generatePath, useHistory, useParams } from "react-router-dom";

import { getSearchTopWidgets } from "@/api/endpoints/searches";
import { postTrackingData } from "@/api/endpoints/track";
import { METRIC, Metric } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import useDragScroll from "@/hooks/useDragScroll";
import { useUser } from "@/hooks/useUser";
import { RELATIVE_PATHS } from "@/routes_new";
import { EVENT } from "@/tracking_events";
import { SearchFiltersFull } from "@/types/SearchFilter";
import { TopWidgetData } from "@/types/TopWidgetData";
import { User } from "@/types/User";
import { cx, highlightElement, pluralize, round, stringifyUrl } from "@/utils";
import Badge from "@components/Badge";
import Tooltip from "@components/Tooltip";
import Typography from "@components/Typography";

import Card from "./partials/Card";
import InsightsSummary from "./partials/InsightsSummary";

import styles from "./TopWidgets.module.css";

export type TopWidgetsProps = {
  className?: string;
  isPublicInsightsPage?: boolean;
  isInsightsPage: boolean;
  alertUuid: string;
  filters: Partial<SearchFiltersFull>;
  url: string;
};

const Trend: React.FunctionComponent<{
  then: number;
  now: number;
}> = ({ then, now }) => {
  // Would result in infinity. Can't compare with nothing.
  if (then === 0)
    return (
      <Typography margin="none" className={styles.trendText}>
        &nbsp;
      </Typography>
    );

  const diff = now - then;
  const percent = diff / then;

  const change = Math.abs(Math.round(percent * 100));
  const isPositive = percent > 0;
  const sign = isPositive ? "+" : "-";

  return (
    <div className={styles.trendContainer}>
      <Badge
        className={styles.trendBadge}
        variant={isPositive ? "success" : "error"}
      >
        {sign}
        {change}%
      </Badge>

      <Typography margin="none" className={styles.trendText}>
        {L.SEARCH_TOP_WIDGET_COMPARISON}
      </Typography>
    </div>
  );
};

const PreviousPeriod: React.FunctionComponent<{
  value: number;
}> = ({ value }) => {
  const isPositive = value > 0;
  const sign = isPositive ? "+" : value < 0 ? "-" : "";
  return (
    <div className={styles.previousPeriodContainer}>
      <Typography margin="none" className={styles.previousPeriodText}>
        {L.SEARCH_TOP_WIDGET_PREVIOUS_PERIOD}
      </Typography>

      <Badge
        className={styles.previousPeriodBadge}
        variant={isPositive ? "success" : "error"}
      >
        {`${sign}${Math.abs(value)}%`}
      </Badge>
    </div>
  );
};

const TopWidgets: React.FunctionComponent<TopWidgetsProps> = ({
  className,
  isInsightsPage,
  isPublicInsightsPage,
  alertUuid,
  filters,
  url,
}) => {
  const [data, setData] = useState<TopWidgetData | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { previous, current } = data || {};
  const ref = useDragScroll<HTMLDivElement>({ x: true, y: false });
  const { abbreviateNumber } = useI18n();
  const history = useHistory();
  const { data: user } = useUser();
  const userCurrency = (user?.data as User)?.currency;
  const { currency } = useI18n();
  const { orgUuid } = useParams<{ orgUuid: Uuid }>();

  useEffect(() => {
    const controller = new AbortController();
    setError(false);
    setLoading(true);
    getSearchTopWidgets(
      alertUuid,
      filters,
      userCurrency || currency,
      controller.signal
    )
      .then((res) => {
        if (controller.signal.aborted) return;
        if (res.ok && res.data) {
          setLoading(false);
          setError(false);
          setData(res.data);
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          setLoading(false);
          setError(error);
        }
      });

    return () => controller.abort();
  }, [alertUuid, currency, filters, userCurrency]);

  function getCardUrl(metric: string) {
    return stringifyUrl({
      url:
        generatePath(`${url}${RELATIVE_PATHS.ALERT_INSIGHTS}/`, {
          uuid: alertUuid,
          orgUuid,
        }) +
        "#" +
        metric,
      query: filters,
    });
  }

  async function handleClickCard(metric: Metric) {
    if (isInsightsPage) {
      const element = document.getElementById(metric);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        highlightElement(element);
      }
      return;
    }

    postTrackingData(EVENT.TOGGLE_ALERT_ANALYTICS, {
      to: "analytics",
    });

    history.push(getCardUrl(metric));
  }

  if (error) return null;

  return (
    <div
      className={cx(styles.root, className, { [styles.blue]: isInsightsPage })}
    >
      <div className={styles.wrapper}>
        <div
          ref={ref}
          className={cx(styles.grid, styles.fixedHeight, {
            [styles.loading]: loading,
            [styles.gridCenter]: isPublicInsightsPage,
          })}
          data-testid="search-topwidgets"
        >
          {previous && current && (
            <>
              <Card
                onClick={() =>
                  handleClickCard(METRIC.INSIGHTS_NUM_MENTIONS_OVER_TIME)
                }
                loading={loading}
              >
                <Typography className={styles.title} margin="none">
                  {L.SEARCH_TOP_WIDGET_TITLE_MENTIONS}
                </Typography>
                <div
                  className={styles.header}
                  data-testid="search-topwidgets-mentions-value"
                >
                  <Typography
                    variant="h2"
                    margin="none"
                    className={styles.heading}
                  >
                    {abbreviateNumber(current.mention_count)}
                  </Typography>

                  <span className={styles.episodeCount}>
                    {" "}
                    {pluralize(
                      current?.episode_count,
                      L.SEARCH_TOP_WIDGET_TITLE_EPISODES_SINGULAR,
                      L.SEARCH_TOP_WIDGET_TITLE_EPISODES_PLURAL.replace(
                        "{}",
                        current?.episode_count
                      )
                    )}
                  </span>
                </div>
                <Trend
                  then={previous?.mention_count}
                  now={current?.mention_count}
                />
              </Card>

              <Card
                onClick={() => handleClickCard(METRIC.INSIGHTS_REACH_OVER_TIME)}
                loading={loading}
              >
                <Typography className={styles.title} margin="none">
                  {L.SEARCH_TOP_WIDGET_TITLE_REACH}
                </Typography>

                <Typography
                  variant="h2"
                  margin="none"
                  data-testid="search-topwidgets-reach-value"
                  className={styles.heading}
                >
                  {abbreviateNumber(round(current.reach))}
                </Typography>
                <Trend then={previous.reach} now={current.reach} />
              </Card>

              <Card
                onClick={() =>
                  handleClickCard(METRIC.INSIGHTS_PR_VALUE_OVER_TIME)
                }
                loading={loading}
              >
                <Typography className={styles.title} margin="none">
                  {L.SEARCH_TOP_WIDGET_TITLE_PR_VALUE} (
                  {userCurrency || currency})
                </Typography>
                <Typography
                  variant="h2"
                  margin="none"
                  data-testid="search-topwidgets-pr-value-value"
                  className={styles.heading}
                >
                  {abbreviateNumber(round(current.pr_value))}
                </Typography>
                <Trend then={previous?.pr_value} now={current?.pr_value} />
              </Card>

              <Card
                onClick={() =>
                  handleClickCard(
                    METRIC.INSIGHTS_SENTIMENT_OVER_TIME_PER_MEDIA_CATEGORY
                  )
                }
                loading={loading}
              >
                <Typography className={styles.title} margin="none">
                  {L.SEARCH_TOP_WIDGET_TITLE_NET_SENTIMENT}
                </Typography>
                <Typography
                  variant="h2"
                  margin="none"
                  className={cx(
                    {
                      [styles.positiveColor]: current.net_sentiment.value > 0,
                      [styles.negativeColor]: current.net_sentiment.value < 0,
                      [styles.neutralColor]: current.net_sentiment.value === 0,
                    },
                    styles.heading
                  )}
                  data-testid="search-topwidgets-net-sentiment-value"
                >
                  {current.net_sentiment.value > 0 ? "+" : ""}
                  {current.net_sentiment.value}%
                </Typography>
                <Tooltip
                  content={
                    <>
                      {`${L.CUSTOMERS_SENTIMENT_GRAPH_POSITIVE}: ${current.net_sentiment.percentage_positive}%`}
                      <br />
                      {`${L.CUSTOMERS_SENTIMENT_GRAPH_NEUTRAL}: ${current.net_sentiment.percentage_neutral}%`}
                      <br />
                      {`${L.CUSTOMERS_SENTIMENT_GRAPH_NEGATIVE}: ${current.net_sentiment.percentage_negative}%`}
                    </>
                  }
                >
                  <div className={styles.sentimentBar}>
                    <div
                      className={styles.positiveBg}
                      style={{
                        width: `${current.net_sentiment.percentage_positive}%`,
                      }}
                    />
                    <div
                      className={styles.neutralBg}
                      style={{
                        width: `${current.net_sentiment.percentage_neutral}%`,
                      }}
                    />
                    <div
                      className={styles.negativeBg}
                      style={{
                        width: `${current.net_sentiment.percentage_negative}%`,
                      }}
                    />
                  </div>
                </Tooltip>
                <PreviousPeriod value={previous.net_sentiment.value} />
              </Card>

              <InsightsSummary className={styles.insightsSummary} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopWidgets;
